import React, {
  RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  VFC,
} from 'react';

import { AxiosInstance } from 'axios';
import classNames from 'classnames';
import { Snackbar } from 'mintel-react-ui';

import Article from '/components/Article';
import InPageNav from '/components/InPageNav';
import LongTermPreviewBanner from '/components/LongTermPreviewBanner/LongTermPreviewBanner';
import Taskbar from '/components/Taskbar';
import { ArticleBlockJson, SendSnackbarEventFn, SnackbarEvent } from '/types';
import {
  MetadataContext,
  PresentModeContext,
  PublishStatusContext,
  UserAccessRestrictionContext,
} from '/utils/contexts';
import { calculateScrollPercentage, getSections } from '/utils/functions';
import usePresentMode from '/utils/usePresentMode';
import useTracking from '/utils/useTracking';

import styles from './ContentPage.scss';
import useScrollToBlock from './useScrollToBlock';

type ContentPageProps = {
  cwaAxios: AxiosInstance;
  content: ArticleBlockJson[];
  contentType: string;
  blockNum?: string;
  groupId?: string;
  articleId?: string;
};

const ContentPage: VFC<ContentPageProps> = ({
  cwaAxios,
  content,
  contentType,
  blockNum,
  groupId,
  articleId,
}) => {
  const metadata = useContext(MetadataContext);
  const { trackScroll, trackArticleView, trackPresentMode } = useTracking();
  const ref = useRef(Array(0)); // Don't rely on the length.
  const accessRestrictions = useContext(UserAccessRestrictionContext);

  const {
    presentMode,
    handlePresent,
    onScreenBlock,
    presentationRef,
  } = usePresentMode(ref, trackPresentMode);

  const publishStatus = useContext(PublishStatusContext);

  const [snackbarEvents, setSnackbarEvents] = useState<SnackbarEvent[]>([]);

  useScrollToBlock(content, ref, blockNum);

  const visibleBlocks = useMemo((): ArticleBlockJson[] => {
    // hidden blocks filtered out
    return presentMode
      ? content.filter((block) => !block?.presentation_options?.hide)
      : content;
  }, [content, presentMode]);

  const sections = useMemo(() => getSections(visibleBlocks), [visibleBlocks]);

  useEffect(() => {
    const onScroll = (): void => trackScroll(calculateScrollPercentage());
    window.addEventListener('scroll', onScroll, { passive: true });
    return (): void => window.removeEventListener('scroll', onScroll);
  }, [trackScroll]);

  useEffect(() => {
    document.title = `${metadata.title} - Mintel`;
  }, [metadata.title]);

  useEffect(() => {
    trackArticleView();
    // We really only want this to run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendSnackbarEvent: SendSnackbarEventFn = (message: string) => {
    setSnackbarEvents([
      ...snackbarEvents,
      { key: `Idx-${snackbarEvents.length}`, message: message },
    ]);
  };

  return (
    <PresentModeContext.Provider value={presentMode}>
      <div
        ref={presentationRef as RefObject<HTMLDivElement>}
        className={classNames({
          [styles['present-mode']]: presentMode,
        })}
      >
        {publishStatus === 'long_term_preview' && !presentMode && (
          <LongTermPreviewBanner publishedDate={metadata.date.published} />
        )}
        <div className={styles['content-page']}>
          {!presentMode && (
            <>
              <InPageNav
                blockRefs={ref}
                databookUrl={
                  !accessRestrictions.isLibraryLockdownUser
                    ? metadata?.interactive_databook
                    : ''
                }
                sections={sections}
                title={metadata.title}
                groupId={groupId}
                articleId={articleId}
              />
              <Taskbar
                cwaAxiosArticle={cwaAxios}
                contentType={contentType}
                handlePresent={handlePresent}
              />
            </>
          )}
          <Article
            content={visibleBlocks}
            ref={ref}
            onScreenBlock={onScreenBlock}
            sendSnackbarEvent={sendSnackbarEvent}
          />
          <div className={styles['snackbar']}>
            <Snackbar events={snackbarEvents} />
          </div>
        </div>
      </div>
    </PresentModeContext.Provider>
  );
};

ContentPage.displayName = 'components/ContentPage';
export default ContentPage;
